import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import cx from "classnames";
import _ from "lodash";
import { toast } from "react-toastify";
import { useStoreApi } from "@xyflow/react";
import { IKImage } from "imagekitio-react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { Button, Dropdown, Form } from "react-bootstrap";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { processRequest } from "services/filters.service";
import { getRecursiveChildNodes } from "../chartEditor/helper";
import { FailureToast } from "components/Toast";
import Tooltip from "components/Tooltip";
import {
  createJourneyStep,
  deleteJourneyStep,
  updateJourneyStep,
} from "actions/journey";
import storyboardRoleService from "services/storyboardRoles.service";

import styles from "./nodes.module.scss";

const defaultContentImage = "/public/cleverstory/v2/static/default_card.jpg";

export const AssetNode = ({ data, id }) => {
  const dispatch = useDispatch();

  const { handleSetValue, handleToggleContentManager, value, description} = data;
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const [assetMode, setAssetMode] = useState({
    value: "DYNAMIC",
    label: "Dynamic Selection",
  });
  const isDynamicNode = (assetMode.value === "DYNAMIC" && description);
  const isManualNode = (assetMode.value === "MANUAL" && value);
  const store = useStoreApi();
  const { nodes, edges } = store.getState();

  const assetOptions = [
    {
      value: "MANUAL",
      label: "Manual Selection",
    },
    {
      value: "DYNAMIC",
      label: "Dynamic Selection",
    },
    // {
    //   value: "storyboard",
    //   label: "This storyboard",
    // },
    // {
    //   value: "section",
    //   label: "Section",
    // },
  ];
  const instance = useReactFlow();

  let { journeyId } = useParams();
  const journeySteps =
    useSelector((state) => _.get(state, `journey.${journeyId}.steps`, [])) ||
    [];
  const nodeStep = _.find(journeySteps, function (step) {
    return step.uid == id || step.id === id;
  });

  useEffect(() => {
    if (!nodeStep || nodeStep.loading) return;
    
    const mappingType = nodeStep?.mappingType;
    const isDynamicType = mappingType === 'DYNAMIC';
    const hasFilters = !!nodeStep.filters;
    const isManual = mappingType ? (!hasFilters && !isDynamicType) : (!hasFilters);
    const isDynamic = mappingType ? (hasFilters && isDynamicType) : (hasFilters);
  
    if (isManual) {
      setAssetMode({
        value: "MANUAL",
        label: "Manual Selection",
      });
    } else if (isDynamic){
      setAssetMode({
        value: "DYNAMIC",
        label: "Dynamic Selection",
      });
    }
  }, [nodeStep]);
  

  useEffect(() => {
    if (nodeStep && nodeStep) {
      handleSetValue(id, nodeStep);
    }
  }, [nodeStep]);

  const triggerNode = useCallback(() => {
    const currentDetails = _.find(nodes, (node) => {
      return node.id == id;
    });
    return currentDetails.parentNode;
  }, [store]);

  const AssetSelection = () => {
    const startNode = !triggerNode();

    return (
      <>
        <DropDownOption
          assetOptions={assetOptions}
          assetMode={assetMode}
          setAssetMode={setAssetMode}
          startNode={startNode}
          triggerNode={triggerNode}
        />
        <div className={cx(styles.assetDetails, { [styles.selected]: isDynamicNode })}>
          {(!isDynamicNode && !isManualNode) ? (
            <Button
              onClick={() => {
                handleToggleContentManager({
                  selectedNode: id,
                  show: true,
                  type: assetMode.value,
                });
              }}
              variant="info"
              className={styles.addAsset}
            >
              <i className={cx(styles.icon, "cleverstory-plus")} />
              Choose
            </Button>
          ) :
          <AssetCard isDynamicNode={isDynamicNode} description={description}/> }
        </div>
      </>
    );
  };

  const DropDownOption = ({
    assetOptions,
    assetMode,
    setAssetMode,
    startNode,
    triggerNode,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const toggleDropdown = (e) => {
      e.stopPropagation(); // Prevent event bubbling
      setIsOpen((prev) => !prev);
    };

    const handleOptionClick = (option) => {
      setAssetMode(option); // Callback for the selected option
      setIsOpen(false); // Close the dropdown after selection
    };

    // Close the dropdown if clicked outside
    React.useEffect(() => {
      const handleClickOutside = (event) => {
        console.log('event clicked', event.target, dropdownRef.current)
        setIsOpen(false);
      };
      document.addEventListener("click", handleClickOutside);
      return () =>
        document.removeEventListener("click", handleClickOutside);
    }, []);

    return (
      <div
        ref={dropdownRef}
        className={cx(styles.selectionDropdown)}
      >
        <button
          onClick={toggleDropdown}
          className={cx(styles.toggle, { [styles.open]: isOpen })}
        >
          <TruncatedTextWithTooltip
            placement="bottom"
            className={styles.name}
            content={_.get(assetMode, "label", "Select")}
          />
          <i className={cx(styles.icon, "cleverstory-drop-down", {[styles.dropdownOpen]: isOpen})} />
        </button>
        {isOpen && (
          <div className={styles.menu}>
            {assetOptions.map((option, index) => (
              <div
                key={index}
                className={styles.menuItem}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const AssetCard = ({isDynamicNode, description}) => {
    const getFieldSelector = (mapping, key) => {
      const fields = _.get(mapping, "fields") || [];
      const field = fields.find((item) => item.name === key);

      return field;
    };

    const getContentForFieldSelector = (mapping, key) => {
      const field = getFieldSelector(mapping, key);

      if (!field) {
        return null;
      }

      return _.get(field, `content`);
    };

    const handleClick = (e) => {
      handleToggleContentManager({
        selectedNode: nodeStep.id,
        show: true,
        type: assetMode.value,
      });
    };

    var content =
      _.get(value, "content.asset", "") ||
      _.get(value, "content", "") ||
      _.get(value, "asset", "");
    var iconData =
      getContentForFieldSelector(value, "icon") || _.get(content, "icon", "");

    var iconUrl = _.get(iconData, "url", null) || _.get(iconData, "key", null);

    return (
      <div onClick={handleClick} className={cx(styles.assetCardWrapper, {[styles.DynamicAssetCardWrapper]: isDynamicNode})}>
          {isDynamicNode ? 
            <div className={cx({[styles.multipleAssetLayer]: isDynamicNode})}>
              <Tooltip 
                placement="top" 
                content={<div dangerouslySetInnerHTML={{ __html: description }} className={styles.tooltipFilterContent}/>} 
                className={styles.tooltip}>
                <div dangerouslySetInnerHTML={{ __html: description }} className={styles.filterContent}/>
              </Tooltip>
            </div> :
            <>
              <div className={styles.iconWrapper}>
                <IKImage
                  className={styles.icon}
                  path={iconUrl || defaultContentImage}
                />
              </div>
              <div className={styles.detailsContainer}>
                {content.name && (
                  <>
                    <TruncatedTextWithTooltip
                      placement="top"
                      maxWidth={100}
                      className={styles.name}
                      content={content.name}
                    />
                    <div className={styles.contentMetaData}>
                      <span className={styles.fileType}>
                        {content?.metadata?.extension}
                      </span>
                      {content?.metadata?.extension == "pdf" && (
                        <span className={styles.border}></span>
                      )}
                      {content?.metadata?.extension == "pdf" && (
                        <span className={styles.pages}>
                          {content?.metadata?.pages} PAGES
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          }
        </div>
    );
  };

  const handleDeleteStep = useCallback(() => {
    if (id) {
      instance.deleteElements({ nodes: [{ id: id }] });

      if (nodeStep && nodeStep.id) {
        let deleteMappings = getRecursiveChildNodes(nodes, id);
        deleteMappings = [...deleteMappings, nodeStep.id];
        const stepsData = _.map(journeySteps, "id");
        const deleteRequest = _.filter(deleteMappings, (id) =>
          _.includes(stepsData, id)
        );
        dispatch(deleteJourneyStep(journeyId, deleteRequest));
      }
    }
  }, [store, nodeStep]);

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        draggable={false}
        style={{
          top: 0,
          btop: "auto",
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
      <div className={cx(styles.assetNode, {[styles.recommendAssetNode]: triggerNode()}, {[styles.disabled] : !storyboardRoleService.canEditStoryboard(storyboard)})}>
        <div className={styles.headAction}>
          <span className={styles.title}>
            {!triggerNode() ? "Trigger" : "Recommend"}
          </span>
          {triggerNode() && (
            <Button
              variant="info"
              onClick={handleDeleteStep}
              className={styles.deleteButton}
            >
              <i className={cx(styles.icon, "cleverstory-trash")} />
            </Button>
          )}
        </div>
        <div className={cx(styles.contentView, {[styles.contentViewMultipleAsset]: isDynamicNode})}>
          <AssetSelection />
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        draggable={false}
        style={{
          bottom: 0,
          top: "auto",
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
    </>
  );
};

export const ConditionNode = ({ id, data }) => {
  const dispatch = useDispatch();
  const store = useStoreApi();

  const instance = useReactFlow();
  const { handleSetValue, value } = data;
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const { nodes, edges } = store.getState();

  let { journeyId } = useParams();
  const journeySteps =
    useSelector((state) => _.get(state, `journey.${journeyId}.steps`, [])) ||
    [];

  const nodeStep = _.find(journeySteps, function (step) {
    return step.uid === id || step.id === id;
  });

  const dropDownOptions = [
    {
      label: "Time spent is less than",
      property: {
        type: "TIME",
      },
      operator: "IS_LESS_THAN",
      value: 30,
    },
    {
      label: "Percentage is less than",
      property: {
        type: "PERCENTAGE",
      },
      operator: "IS_LESS_THAN",
      value: 50,
    },
  ];

  useEffect(() => {
    if (nodeStep && nodeStep.filters) {
      handleSetValue(id, nodeStep);
    }
  }, [nodeStep]);

  const handleUpdateCondition = (option) => {
    var commandsRequest = processRequest([option]);
    commandsRequest = (commandsRequest || []).filter(
      (item) =>
        item.operator === "IS_ALL" || (item.value && item.value.length > 0)
    );

    var conditionStepRequest = {
      id: journeyId,
      type: "CONDITION",
      commands: [
        {
          property: option.property,
          operator: option.operator,
          value: [`${option.value}`],
        },
      ],
    };

    if (!nodeStep) {
      dispatch(createJourneyStep(journeyId, id, conditionStepRequest));
    } else {
      dispatch(updateJourneyStep(journeyId, nodeStep.id, conditionStepRequest));
    }
  };

  const handleDeleteStep = () => {
    if (id) {
      instance.deleteElements({ nodes: [{ id: id }] });
      if (nodeStep && nodeStep.id) {
        let deleteMappings = getRecursiveChildNodes(nodes, id);
        deleteMappings = [...deleteMappings, nodeStep.id];
        const stepsData = _.map(journeySteps, "id");
        const deleteRequest = _.filter(deleteMappings, (id) =>
          _.includes(stepsData, id)
        );

        dispatch(deleteJourneyStep(journeyId, deleteRequest));
      }
    }
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        draggable={false}
        id="top_target"
        style={{
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        draggable={false}
        id="no_source"
        style={{
          left: "-26px",
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
      <div className={cx(styles.conditionNode ,{[styles.disabled] : !storyboardRoleService.canEditStoryboard(storyboard)})}>
        <div
          className={cx(styles.actionContainer, {
            [styles.changeLayout]: value,
          })}
        >
          <Dropdown className={styles.conditionDropdown}>
            <Dropdown.Toggle className={styles.toggle}>
              <DropdownToggleWithInput
                condition={value}
                handleUpdateCondition={handleUpdateCondition}
              />
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.options}>
              {dropDownOptions.map((option) => {
                return (
                  <Dropdown.Item onClick={() => handleUpdateCondition(option)}>
                    {option.property.type == "TIME"
                      ? `${option.label}`
                      : `${option.label}`}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          <Button
            variant="info"
            onClick={handleDeleteStep}
            className={styles.deleteButton}
          >
            <i className={cx(styles.icon, "cleverstory-trash")} />
          </Button>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        draggable={false}
        id="yes_source"
        style={{
          right: "-26px",
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
    </>
  );
};

const DropdownToggleWithInput = ({ condition, handleUpdateCondition }) => {
  const [inputValue, setInputvalue] = useState(null);

  useEffect(() => {
    if (condition && !_.isEqual(inputValue, condition.value)) {
      setInputvalue(condition.value);
    }
  }, [condition]);

  const validateInput = () => {
    const rules = {
      TIME: {
        regex: /^\d*$/, // Only allows non-negative numbers
        min: 0, // No negative values allowed
        error: "Enter a valid non-negative numeric value for time",
      },
      PERCENTAGE: {
        regex: /^\d*$/,
        min: 0,
        max: 100,
        error: "Percentage must be a number between 0 and 100",
      },
    };

    const rule =
      rules[condition.property.type === "TIME" ? "TIME" : "PERCENTAGE"];

    // Check if the input matches regex and is within range if defined
    const isValid =
      rule.regex.test(inputValue) &&
      (rule.min === undefined || inputValue >= rule.min) &&
      (rule.max === undefined || inputValue <= rule.max);

    return {
      isValid,
      error: isValid ? "" : rule.error,
    };
  };

  const handleValueChange = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      const req = {
        ...condition,
        value: inputValue,
      };

      const { isValid, error } = validateInput();

      if (!isValid) {
        toast(<FailureToast heading="Error" description={error} />, {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        });
        setInputvalue(condition.value); // Revert to the last valid value
        return;
      }

      if (!_.isEqual(condition.value, [`${inputValue}`])) {
        handleUpdateCondition(req);
        return;
      }
    }
  };

  return (
    <>
      {condition ? (
        <div className={styles.inlineToggle}>
          <span className={styles.label}>
            {condition.property.type === "TIME"
              ? `Time spent is less than`
              : `Percentage is less than`}
          </span>
          <div className={styles.inputWrapper}>
            <Form.Control
              type="text"
              value={inputValue}
              onKeyDown={handleValueChange} // Trigger update on Enter
              onChange={(e) => {
                setInputvalue(e.target.value);
              }}
              onBlur={handleValueChange}
              className={styles.inlineInput}
            />
            <span>{condition.property.type === "TIME" ? "s" : "%"}</span>
          </div>
        </div>
      ) : (
        <span>Select a condition</span>
      )}
    </>
  );
};

export const SelectorNode = ({ id, data, sourceHandle }) => {
  const [expand, setExpand] = useState(false);
  const { handleAddNode, disabled, source } = data;
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const handleExpandOption = (state) => {
    setExpand(state);
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        draggable={false}
        id={id}
        style={{
          top: 0,
          top: "auto",
          border: "none",
          minHeight: "0px",
          minWidth: "0px",
          width: "0px",
          height: "0px",
        }}
      />
      <div
        className={cx(styles.selectorNode, {
          [styles.expandOptions]: expand,
          [styles.selectorNodeDisabled]: disabled,
          [styles.disabled] : !storyboardRoleService.canEditStoryboard(storyboard)
        })}
      >
        {!expand && (
          <Button
            onClick={() => handleExpandOption(true)}
            variant="info"
            disabled={disabled}
            className={styles.addButton}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
          </Button>
        )}
        {expand && (
          <>
            <Button
              onClick={() => handleExpandOption(false)}
              variant="info"
              className={styles.closeButton}
            >
              <i className={cx(styles.icon, "cleverstory-close")} />
            </Button>
            <div className={styles.optionsContainer}>
              {source !== "conditionNode" && (
                <div
                  onClick={() => handleAddNode(id, "conditionNode")}
                  className={styles.nodeButton}
                >
                  <i className={cx(styles.icon, "cleverstory-unchecked-box")} />
                  <span>Condition</span>
                </div>
              )}
              <div
                onClick={() => handleAddNode(id, "assetNode")}
                className={styles.nodeButton}
              >
                <i className={cx(styles.icon, "cleverstory-doc-inline")} />
                <span>Assets</span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
